import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { injectIntl } from 'react-intl';
import { useViewlogies } from 'context';
import {
    getAdjectives,
    submitAdjectives,
    getCharacterCloudCookie,
    setCharacterCloudCookie,
} from 'services/characterCloud';
import { getViewerContactCookie } from 'services/guest';
import ReCAPTCHA from 'react-google-recaptcha';
import config from 'config/default';
import { FadeLoader } from 'react-spinners';
import Button from 'components/Button';
import { USE_MOCK_DATA } from './config';
import './characterCloud.scss';

const CharacterCloudForm = ({ intl, firstName, color, onSuccess, fetchCharacterCloud }) => {
    const {
        event: { id, organizationId },
    } = useViewlogies();

    const [adjectiveCount, setAdjectiveCount] = useState(3);
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);
    const [activeInputIndex, setActiveInputIndex] = useState(null);
    const [message, setMessage] = useState('');
    const [isRecaptchaError, setIsRecaptchaError] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(getCharacterCloudCookie(id) === 'true');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchQueries, setSearchQueries] = useState({});
    const [focusedInputIndex, setFocusedInputIndex] = useState(null);
    const [emailError, setEmailError] = useState('');
    const [adjectiveErrors, setAdjectiveErrors] = useState({}); // Track errors per adjective input index
    const [validAdjectives, setValidAdjectives] = useState(new Set()); // Track adjectives selected from dropdown
    const recaptchaRef = useRef(null);
    const lastUpdatedIndexRef = useRef(null); // Track the last input index that was updated
    const requestCounterRef = useRef(0); // Track request order to prevent stale responses

    const loaderStyles = `
        margin-left: 20px;
    `;

    // Fetch autocomplete options when user types
    useEffect(() => {
        const fetchAutocomplete = async (query, index, requestId) => {
            if (!query || query.length < 1) {
                setAutocompleteOptions([]);
                setActiveInputIndex(null);
                return;
            }

            try {
                const { data } = await getAdjectives(organizationId, id, query);
                // Verify that this request is still the most recent one
                if (requestId !== requestCounterRef.current) {
                    // This is a stale response, ignore it
                    return;
                }

                if (data && data.adjectives && data.adjectives.length > 0) {
                    setAutocompleteOptions(data.adjectives);
                    setActiveInputIndex(index);
                } else {
                    setAutocompleteOptions([]);
                    setActiveInputIndex(null);
                }
            } catch (error) {
                // Only log error if this is still the most recent request
                if (requestId === requestCounterRef.current) {
                    console.error('Error fetching adjectives:', error);
                    setAutocompleteOptions([]);
                    setActiveInputIndex(null);
                }
            }
        };

        // Determine which input to fetch autocomplete for
        // Priority: 1) focusedInputIndex, 2) lastUpdatedIndexRef (most recent change), 3) activeInputIndex
        let currentIndex = null;
        let query = null;

        // First, try to use focused input index (most reliable - user is actively focused)
        if (focusedInputIndex !== null && searchQueries[focusedInputIndex]) {
            currentIndex = focusedInputIndex;
            query = searchQueries[focusedInputIndex];
        }
        // If no focused input, use the last updated index (most recent change)
        else if (lastUpdatedIndexRef.current !== null && searchQueries[lastUpdatedIndexRef.current]) {
            currentIndex = lastUpdatedIndexRef.current;
            query = searchQueries[lastUpdatedIndexRef.current];
        }
        // Fall back to activeInputIndex if it still has a valid query
        else if (activeInputIndex !== null && searchQueries[activeInputIndex]) {
            currentIndex = activeInputIndex;
            query = searchQueries[activeInputIndex];
        }

        // Debounce autocomplete
        const timeoutId = setTimeout(() => {
            if (query && query.length > 0 && currentIndex !== null) {
                // Don't fetch autocomplete if this value is already a valid selected adjective
                // (prevents dropdown from reappearing after selection)
                if (validAdjectives.has(query.toLowerCase())) {
                    setAutocompleteOptions([]);
                    setActiveInputIndex(null);
                    return;
                }
                // Increment request counter for this new request
                requestCounterRef.current += 1;
                const requestId = requestCounterRef.current;
                fetchAutocomplete(query, currentIndex, requestId);
            } else {
                setAutocompleteOptions([]);
                setActiveInputIndex(null);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQueries, focusedInputIndex, activeInputIndex, organizationId, id]);

    const handleInputChange = (index, value, setFieldValue) => {
        setFieldValue(`adjective${index}`, value);
        setSearchQueries(prev => ({ ...prev, [index]: value }));
        // Track which index was just updated
        lastUpdatedIndexRef.current = index;
        setMessage('');
        // Clear error for this specific input when user starts typing
        setAdjectiveErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
        });

        // Clear autocomplete if input is empty
        if (!value || value.length === 0) {
            setAutocompleteOptions([]);
            setActiveInputIndex(null);
        }
    };

    const handleSelectOption = (option, index, setFieldValue) => {
        setFieldValue(`adjective${index}`, option);
        // Remove from searchQueries since this is a final selection, not a search query
        // This prevents the useEffect from triggering autocomplete again
        setSearchQueries(prev => {
            const newQueries = { ...prev };
            delete newQueries[index];
            return newQueries;
        });
        // Mark this adjective as valid (selected from dropdown)
        setValidAdjectives(prev => new Set([...prev, option.toLowerCase()]));
        // Clear error for this input when user selects from dropdown
        setAdjectiveErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
        });
        // Clear all tracking to prevent autocomplete from refetching
        setAutocompleteOptions([]);
        setActiveInputIndex(null);
        setFocusedInputIndex(null);
        lastUpdatedIndexRef.current = null;
    };

    const addAdjectiveField = () => {
        if (adjectiveCount < 5) {
            setAdjectiveCount(prev => prev + 1);
        }
    };

    const removeAdjectiveField = () => {
        if (adjectiveCount > 3) {
            setAdjectiveCount(prev => prev - 1);
        }
    };

    const getFilledAdjectives = values => {
        const adjectives = [];
        for (let i = 0; i < adjectiveCount; i++) {
            const adj = values[`adjective${i}`];
            if (adj && adj.trim()) {
                adjectives.push(adj.trim());
            }
        }
        return adjectives;
    };

    // Validate that an adjective is in the allowed list
    const validateAdjective = async adjective => {
        if (!adjective || !adjective.trim()) return false;
        try {
            // Check if it's in our valid adjectives set (previously selected)
            if (validAdjectives.has(adjective.toLowerCase())) {
                return true;
            }
            // Otherwise, validate against API by searching for exact match
            const { data } = await getAdjectives(organizationId, id, adjective.trim());
            if (data && data.adjectives && Array.isArray(data.adjectives)) {
                // Check if the adjective exactly matches any in the results (case-insensitive)
                const matches = data.adjectives.some(adj => adj.toLowerCase() === adjective.trim().toLowerCase());
                if (matches) {
                    // Add to valid adjectives set
                    setValidAdjectives(prev => new Set([...prev, adjective.trim().toLowerCase()]));
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('Error validating adjective:', error);
            return false;
        }
    };

    if (isFormSubmitted || isSubmitting) {
        return (
            <div className="character-cloud-form-wrapper">
                <section className="character-cloud-form-container">
                    <h1 className="character-cloud-header" style={{ color }}>
                        {intl.formatMessage(
                            {
                                id: 'features.Viewlogy.CharacterCloud.Form.header',
                                defaultMessage: 'When I think of {name}, I think of...',
                            },
                            { name: firstName },
                        )}
                    </h1>
                    {isSubmitting ? (
                        <div className="submission-loader-container">
                            <FadeLoader
                                color={'#646464'}
                                margin={-5}
                                height={10}
                                width={3}
                                radius={1}
                                css={loaderStyles}
                            />
                        </div>
                    ) : (
                        <div className="character-cloud-success-message">
                            <p>
                                {intl.formatMessage({
                                    id: 'features.Viewlogy.CharacterCloud.Form.success',
                                    defaultMessage: 'Your words have been added to the Character Cloud...',
                                })}
                            </p>
                            <Button
                                text={intl.formatMessage({
                                    id: 'features.Viewlogy.CharacterCloud.Form.funeralPlanButton',
                                    defaultMessage: 'Next, interested in the Funeral Plan?',
                                })}
                                className="character-cloud-redirect-button"
                                onClick={() => (window.location.hash = 'funeralGuide')}
                                onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.style.color = color;
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor = color;
                                    e.currentTarget.style.color = '#fff';
                                }}
                                style={{
                                    color: '#fff',
                                    border: `2px solid ${color}`,
                                    backgroundColor: color,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    padding: '12px 24px',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 500,
                                    marginTop: '20px',
                                }}
                            />
                        </div>
                    )}
                </section>
            </div>
        );
    }

    return (
        <div className="character-cloud-form-wrapper">
            <section className="character-cloud-form-container">
                <h1 className="character-cloud-header" style={{ color }}>
                    {intl.formatMessage(
                        {
                            id: 'features.Viewlogy.CharacterCloud.Form.header',
                            defaultMessage: 'When I think of {name}, I think of...',
                        },
                        { name: firstName },
                    )}
                </h1>
                <Formik
                    initialValues={{
                        adjective0: '',
                        adjective1: '',
                        adjective2: '',
                        adjective3: '',
                        adjective4: '',
                        submissionEmail: getViewerContactCookie()?.email || '',
                    }}
                    onSubmit={async (values, { setSubmitting }) => {
                        setMessage('');
                        setEmailError('');
                        setAdjectiveErrors({});
                        const adjectives = getFilledAdjectives(values);

                        if (adjectives.length === 0) {
                            setMessage(
                                intl.formatMessage({
                                    id: 'features.Viewlogy.CharacterCloud.Form.errorNoAdjectives',
                                    defaultMessage: 'Please enter at least one adjective.',
                                }),
                            );
                            setSubmitting(false);
                            return;
                        }

                        // Validate that all adjectives are from the allowed list (selected from dropdown)
                        const invalidAdjectives = [];
                        for (const adj of adjectives) {
                            const isValid = await validateAdjective(adj);
                            if (!isValid) {
                                invalidAdjectives.push(adj);
                            }
                        }

                        if (invalidAdjectives.length > 0) {
                            setMessage(
                                intl.formatMessage(
                                    {
                                        id: 'features.Viewlogy.CharacterCloud.Form.errorInvalidAdjectives',
                                        defaultMessage:
                                            'Please select adjectives from the dropdown. Invalid: {adjectives}',
                                    },
                                    { adjectives: invalidAdjectives.join(', ') },
                                ),
                            );
                            setSubmitting(false);
                            return;
                        }

                        // Validate email is provided
                        const email = values.submissionEmail?.trim() || '';
                        if (!email) {
                            setEmailError(
                                intl.formatMessage({
                                    id: 'features.Viewlogy.CharacterCloud.Form.errorEmailRequired',
                                    defaultMessage: 'Email is required',
                                }),
                            );
                            setSubmitting(false);
                            return;
                        }

                        // Validate email format
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(email)) {
                            setEmailError(
                                intl.formatMessage({
                                    id: 'features.Viewlogy.CharacterCloud.Form.errorEmailInvalid',
                                    defaultMessage: 'Invalid email format',
                                }),
                            );
                            setSubmitting(false);
                            return;
                        }

                        const token = recaptchaRef.current?.getValue();
                        if (!token) {
                            setIsRecaptchaError(true);
                            setSubmitting(false);
                            return;
                        }

                        setIsRecaptchaError(false);
                        setIsSubmitting(true);

                        try {
                            const { status, message: errorMessage } = await submitAdjectives(
                                organizationId,
                                id,
                                adjectives,
                                token,
                                email,
                            );
                            if (status === 201) {
                                // Reset reCAPTCHA after successful submission
                                if (recaptchaRef.current) {
                                    try {
                                        recaptchaRef.current.reset();
                                    } catch (resetError) {
                                        // Silently handle reCAPTCHA reset errors
                                        console.warn('reCAPTCHA reset error (non-critical):', resetError);
                                    }
                                }
                                setCharacterCloudCookie(id);
                                // Only fetch if not using mock data
                                if (!USE_MOCK_DATA && fetchCharacterCloud) {
                                    await fetchCharacterCloud();
                                }
                                setIsSubmitting(false);
                                setIsFormSubmitted(true);
                                if (onSuccess) {
                                    onSuccess();
                                }
                            } else {
                                console.error('There was an error', status);
                                // Handle specific backend error messages
                                if (errorMessage === 'Email is required' || errorMessage === 'Invalid email format') {
                                    setEmailError(errorMessage);
                                } else {
                                    setMessage(
                                        errorMessage ||
                                            intl.formatMessage({
                                                id: 'features.Viewlogy.CharacterCloud.Form.error',
                                                defaultMessage: 'There was an error, please try again.',
                                            }),
                                    );
                                }
                                setIsSubmitting(false);
                            }
                        } catch (error) {
                            console.error('Submission error:', error);
                            setMessage(
                                intl.formatMessage({
                                    id: 'features.Viewlogy.CharacterCloud.Form.error',
                                    defaultMessage: 'There was an error, please try again.',
                                }),
                            );
                            setIsSubmitting(false);
                        }
                        setSubmitting(false);
                    }}
                >
                    {({ values, touched, isSubmitting, setFieldValue }) => {
                        const filledAdjectives = getFilledAdjectives(values);
                        const hasFirstWord = filledAdjectives.length > 0;
                        const email = values.submissionEmail?.trim() || '';
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        const isEmailValid = email && emailRegex.test(email);
                        const isSubmitDisabled = filledAdjectives.length === 0 || isSubmitting || !isEmailValid;

                        return (
                            <Form className="character-cloud-form">
                                {Array.from({ length: adjectiveCount }).map((_, index) => (
                                    <div key={index} className="adjective-input-wrapper">
                                        <label className="adjective-label">
                                            {intl.formatMessage(
                                                {
                                                    id: 'features.Viewlogy.CharacterCloud.Form.adjectiveLabel',
                                                    defaultMessage: 'Adjective #{number}',
                                                },
                                                { number: index + 1 },
                                            )}
                                        </label>
                                        <div className="adjective-input-container">
                                            <Field
                                                name={`adjective${index}`}
                                                className="adjective-input"
                                                placeholder={intl.formatMessage({
                                                    id: 'features.Viewlogy.CharacterCloud.Form.adjectivePlaceholder',
                                                    defaultMessage: 'Start typing a word here',
                                                })}
                                                onChange={e => handleInputChange(index, e.target.value, setFieldValue)}
                                                onFocus={() => {
                                                    setFocusedInputIndex(index);
                                                    lastUpdatedIndexRef.current = index;
                                                    const currentValue = values[`adjective${index}`];
                                                    if (currentValue) {
                                                        setSearchQueries(prev => ({
                                                            ...prev,
                                                            [index]: currentValue,
                                                        }));
                                                    }
                                                }}
                                                onBlur={e => {
                                                    // Delay to allow option click
                                                    setTimeout(() => {
                                                        const value = e.target.value?.trim();
                                                        // If there's a value and it's not in valid adjectives, validate it
                                                        if (value && value.length > 0) {
                                                            // First check if it matches any current autocomplete option
                                                            const matchesAutocomplete = autocompleteOptions.some(
                                                                opt => opt.toLowerCase() === value.toLowerCase(),
                                                            );

                                                            // If it matches an autocomplete option, mark it as valid
                                                            if (matchesAutocomplete) {
                                                                setValidAdjectives(
                                                                    prev => new Set([...prev, value.toLowerCase()]),
                                                                );
                                                            } else if (!validAdjectives.has(value.toLowerCase())) {
                                                                // Not in autocomplete and not previously validated - validate against API
                                                                validateAdjective(value).then(isValid => {
                                                                    if (!isValid) {
                                                                        // Clear invalid custom input
                                                                        setFieldValue(`adjective${index}`, '');
                                                                        setSearchQueries(prev => {
                                                                            const newQueries = { ...prev };
                                                                            delete newQueries[index];
                                                                            return newQueries;
                                                                        });
                                                                        // Set error for this specific input
                                                                        setAdjectiveErrors(prev => ({
                                                                            ...prev,
                                                                            [index]: intl.formatMessage({
                                                                                id: 'features.Viewlogy.CharacterCloud.Form.errorCustomAdjective',
                                                                                defaultMessage:
                                                                                    'Please select an adjective from the dropdown.',
                                                                            }),
                                                                        }));
                                                                    }
                                                                });
                                                            }
                                                        }
                                                        setFocusedInputIndex(null);
                                                        setAutocompleteOptions([]);
                                                        setActiveInputIndex(null);
                                                    }, 200);
                                                }}
                                            />
                                            {activeInputIndex === index && autocompleteOptions.length > 0 && (
                                                <div className="autocomplete-dropdown">
                                                    {autocompleteOptions.map((option, optIndex) => (
                                                        <div
                                                            key={optIndex}
                                                            className="autocomplete-option"
                                                            onClick={() =>
                                                                handleSelectOption(option, index, setFieldValue)
                                                            }
                                                        >
                                                            {option}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {adjectiveErrors[index] && (
                                            <div className="error-message">{adjectiveErrors[index]}</div>
                                        )}
                                    </div>
                                ))}

                                {adjectiveCount < 5 && (
                                    <button
                                        type="button"
                                        className="add-adjective-button"
                                        onClick={addAdjectiveField}
                                        style={{ color }}
                                    >
                                        +{' '}
                                        {intl.formatMessage({
                                            id: 'features.Viewlogy.CharacterCloud.Form.addAdjective',
                                            defaultMessage: 'Add another adjective',
                                        })}
                                    </button>
                                )}

                                {adjectiveCount > 3 && (
                                    <button
                                        type="button"
                                        className="remove-adjective-button"
                                        onClick={removeAdjectiveField}
                                        style={{ color }}
                                    >
                                        -{' '}
                                        {intl.formatMessage({
                                            id: 'features.Viewlogy.CharacterCloud.Form.removeAdjective',
                                            defaultMessage: 'Remove adjective',
                                        })}
                                    </button>
                                )}

                                <div className="submission-email-wrapper">
                                    <label className="submission-email-label">
                                        {intl.formatMessage({
                                            id: 'features.Viewlogy.CharacterCloud.Form.emailLabel',
                                            defaultMessage: 'Your Email',
                                        })}
                                        <span className="required-asterisk">*</span>
                                    </label>
                                    <Field
                                        name="submissionEmail"
                                        type="email"
                                        className="submission-email-input"
                                        placeholder={intl.formatMessage({
                                            id: 'features.Viewlogy.CharacterCloud.Form.emailPlaceholder',
                                            defaultMessage: 'your.email@example.com',
                                        })}
                                        required
                                        onChange={e => {
                                            setFieldValue('submissionEmail', e.target.value);
                                            setEmailError('');
                                            setMessage('');
                                        }}
                                    />
                                    {emailError && <div className="error-message">{emailError}</div>}
                                </div>

                                {hasFirstWord && (
                                    <div className="recaptcha-container">
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={config.recaptchaSiteKey}
                                            onChange={() => {
                                                setMessage('');
                                                setIsRecaptchaError(false);
                                            }}
                                            onErrored={error => {
                                                // Silently handle reCAPTCHA load errors to prevent console spam
                                                console.warn('reCAPTCHA error (non-critical):', error);
                                            }}
                                            onExpired={() => {
                                                // Handle expiration silently
                                                if (recaptchaRef.current) {
                                                    recaptchaRef.current.reset();
                                                }
                                            }}
                                            className="character-cloud-recaptcha"
                                        />
                                        {isRecaptchaError && (
                                            <div className="error-message">
                                                {intl.formatMessage({
                                                    id: 'features.Viewlogy.GuestSection.Form.notARobot',
                                                    defaultMessage: 'Please verify that you are not a robot.',
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {message && !isRecaptchaError && <div className="error-message">{message}</div>}

                                <Button
                                    type="submit"
                                    disabled={isSubmitDisabled}
                                    text={intl.formatMessage({
                                        id: 'features.Viewlogy.CharacterCloud.Form.submit',
                                        defaultMessage: 'Submit',
                                    })}
                                    className="character-cloud-submit-button"
                                    style={{
                                        color: isSubmitDisabled ? '#999' : '#fff',
                                        border: `2px solid ${color}`,
                                        backgroundColor: isSubmitDisabled ? '#f5f5f5' : color,
                                        borderRadius: '6px',
                                        opacity: isSubmitDisabled ? 0.6 : 1,
                                        cursor: isSubmitDisabled ? 'default' : 'pointer',
                                        transition: 'all 0.2s ease',
                                        padding: '12px 24px',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        marginTop: '20px',
                                    }}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </section>
        </div>
    );
};

export default injectIntl(CharacterCloudForm);
